import Vue from "vue"
import { store } from "@/store/stores"
import userService from "./userService"
import trainingFeatureAccess from "./trainingFeatureAccess"
import trainingLiveService from "./trainingLiveService"
import { buildTrainingLiveState, createTrainingSessionId } from "./trainingLiveState"
import { buildLinkedWatchExercises } from "./watchSyncService"
import { sharedWatchProjection, sharedWatchRows, sameWatchRows, mergeWatchRows, phoneWatchSnapshot, phoneWatchCommand } from "./watchWorkoutSession"

const terminal = (state) => !state || ["ending", "completed", "discarded"].includes(state.phase)
const parse = (value) => { try { return typeof value === "string" ? JSON.parse(value) : value } catch (_) { return null } }

class WatchWorkoutLink {
  constructor() {
    this.view = Vue.observable({ state: null, addSessionId: null, error: null, supported: false })
    this.queue = Promise.resolve()
    this.savedSessions = new Set()
    this.openedCourseRequests = new Set()
    this.configuration = 0
    this.handleSaved = (event) => {
      if (event.detail?.userId !== this.userId) return
      this.savedSessions.add(event.detail.clientSessionId)
      if (this.view.state?.clientSessionId === event.detail.clientSessionId) {
        this.view.state = { ...this.view.state, phase: "completed", syncState: "saved" }
        this.receiveShared(this.view.state)
      }
    }
    this.handleEvent = (event) => this.receive(event.detail)
    this.handleReady = () => this.configure()
    this.handleVisible = () => { if (document.visibilityState === "visible") this.configure() }
  }
  async call(payload) {
    const access = trainingFeatureAccess.capture()
    if (access === null && payload.action !== "configure") return null
    const bridge = window.flutter_inappwebview
    if (!bridge?.callHandler) return null
    const userId = access === null ? "" : String(userService.getUserId() || "")
    let timeout
    let response
    try {
      response = await Promise.race([
        bridge.callHandler("watchWorkoutLink", { ...payload, userId, bridgeToken: window.__wellcomeHealthImportToken }),
        new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error("Die Verbindung zur Uhr antwortet noch nicht.")), 10000) }),
      ])
    } finally { clearTimeout(timeout) }
    return (access === null || trainingFeatureAccess.isCurrent(access)) && userId === (access === null ? "" : String(userService.getUserId() || "")) ? response : null
  }
  async start(router) {
    await store.restored
    if (this.unsubscribe) return
    this.router = router
    this.unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === "userStore/setUser") this.configure()
      else if (mutation.type.startsWith("runWorkoutStore/") && !mutation.type.endsWith("SET_LIVE_SESSION")) {
        if (!/SET_LINKED_WATCH_WORKOUT|APPLY_LINKED_WATCH_WORKOUT/.test(mutation.type)) this.sharedChanged(mutation)
        this.schedule()
      }
    })
    this.unsubscribeAccess = trainingFeatureAccess.subscribe(this.handleReady)
    window.addEventListener("watchWorkoutLinkChanged", this.handleEvent)
    window.addEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.addEventListener("healthImportBridgeReady", this.handleReady)
    window.addEventListener("trainingLiveForeground", this.handleReady)
    document.addEventListener("visibilitychange", this.handleVisible)
    window.addEventListener("roody:watch-session-saved", this.handleSaved)
    this.heartbeat = setInterval(() => { this.schedule(); this.sendShared() }, 15000)
    this.configure()
  }
  stop() {
    this.unsubscribe?.(); this.unsubscribe = null
    this.unsubscribeAccess?.()
    this.configuration += 1
    clearInterval(this.heartbeat)
    window.removeEventListener("roody:watch-session-saved", this.handleSaved)
    window.removeEventListener("watchWorkoutLinkChanged", this.handleEvent)
    window.removeEventListener("flutterInAppWebViewPlatformReady", this.handleReady)
    window.removeEventListener("healthImportBridgeReady", this.handleReady)
    window.removeEventListener("trainingLiveForeground", this.handleReady)
    document.removeEventListener("visibilitychange", this.handleVisible)
  }
  async configure() {
    const configuration = ++this.configuration
    const userId = trainingFeatureAccess.isAllowed() ? String(userService.getUserId() || "") : ""
    if (this.userId !== userId) {
      this.userId = userId
      this.savedSessions.clear()
      this.openedCourseRequests.clear()
      this.openingCourseRequest = null
      const active = store.state.runWorkoutStore
      if (active.liveSession?.owner === "watch" && active.liveSession.userId !== userId) store.commit("runWorkoutStore/RESET_WORKOUT_STATE")
      this.view.supported = false
      this.phoneEntrySupported = false; this.phoneSnapshot = null; this.queuedPhonePublish = null
      this.view.state = null; this.view.addSessionId = null; this.pendingCommand = null
    }
    try {
      const response = await this.call({ action: "configure" })
      if (configuration !== this.configuration) return
      this.view.supported = response?.supported === true && Boolean(userId)
      this.phoneEntrySupported = this.view.supported && response?.phoneEntry === true
      this.lastSignature = null
      this.receive(response)
      this.schedule()
      this.sendShared(true)
    } catch (_) { if (configuration === this.configuration) this.view.supported = false }
  }
  receive(response) {
    if (!trainingFeatureAccess.isAllowed() || !response || this.userId !== String(userService.getUserId() || "")) return
    if (response.phoneCommand) this.receivePhoneCommand(response.phoneCommand)
    if (response.pendingCourseOpen) this.openCourseList(response.pendingCourseOpen)
    const state = parse(response.state)
    if (state && (state.userId !== this.userId || state.owner !== "watch" || !state.clientSessionId || !Number.isFinite(state.revision))) return
    const old = this.view.state
    if (state && old?.clientSessionId === state.clientSessionId && old.revision > state.revision) return
    this.view.state = state && this.savedSessions.has(state.clientSessionId) ? { ...state, phase: "completed", syncState: "saved" } : state
    if (state) this.receiveShared(this.view.state)
    if (response.pendingOpen) this.openAdd(response.pendingOpen)
    // Show a newly started Watch workout without replacing an active phone workout.
    if (!response.pendingCourseOpen && state && state.clientSessionId !== old?.clientSessionId && !terminal(state) && !store.state.runWorkoutStore.activeSplit &&
      Date.now() - state.updatedAt < 120000 && this.router?.currentRoute.path.startsWith("/workout")) {
      this.router.push("/workout/hub").catch(() => {})
    }
  }
  async openCourseList(request) {
    const access = trainingFeatureAccess.capture()
    const user = this.userId
    const age = Date.now() - request?.requestedAt
    if (access === null || !user || user !== String(userService.getUserId() || "") || request?.userId !== user ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(request?.requestId || "") ||
      !Number.isFinite(request?.requestedAt) || age < -60000 || age > 300000 ||
      document.visibilityState !== "visible" || !this.router || this.openingCourseRequest === request.requestId) return false
    this.openingCourseRequest = request.requestId
    try {
      if (!this.openedCourseRequests.has(request.requestId)) {
        if (this.router.currentRoute.path !== "/kurse") await this.router.push("/kurse")
        if (!trainingFeatureAccess.isCurrent(access) || user !== this.userId || this.router.currentRoute.path !== "/kurse") return false
        this.openedCourseRequests.add(request.requestId)
        if (this.openedCourseRequests.size > 100) this.openedCourseRequests.delete(this.openedCourseRequests.values().next().value)
      }
      await this.call({ action: "courseHandled", requestId: request.requestId })
      return true
    } catch (_) {
      // Keep the native request pending if navigation or the acknowledgement failed.
      return false
    } finally {
      if (this.openingCourseRequest === request.requestId) this.openingCourseRequest = null
    }
  }
  schedule() {
    if (this.scheduled) return
    this.scheduled = true
    Promise.resolve().then(() => {
      this.scheduled = false
      if (!this.view.supported || !trainingFeatureAccess.isAllowed()) return
      trainingLiveService.ensureSession()
      const workout = store.state.runWorkoutStore
      if (workout.liveSession?.owner === "watch") return
      let state = buildTrainingLiveState(workout, this.userId)
      if (workout.liveSession?.savedAt) state = null
      if (this.phoneEntrySupported) {
        try { state = phoneWatchSnapshot(workout, this.userId, this.phoneSnapshot) } catch (_) { state = null }
        if (state && new TextEncoder().encode(JSON.stringify(state)).length > 63000) state = null
        this.phoneSnapshot = state
      }
      const signature = JSON.stringify(state && { ...state, revision: 0, updatedAt: 0, elapsedAnchorAt: 0,
        elapsedSeconds: state.phase === "ending" ? state.elapsedSeconds : Math.round((state.elapsedAnchorAt / 1000 - state.elapsedSeconds)) })
      if (signature === this.lastSignature && (!state || Date.now() - this.lastPublishAt < 14000)) return
      this.lastPublishAt = Date.now()
      this.lastSignature = signature
      this.queuedPhonePublish = { user: this.userId, action: this.phoneEntrySupported ? "publishWorkout" : "publish", state }
      this.flushPhonePublish()
    })
  }
  async flushPhonePublish() {
    if (this.publishingPhone) return
    this.publishingPhone = true
    try {
      while (this.queuedPhonePublish) {
        const { user, ...payload } = this.queuedPhonePublish
        this.queuedPhonePublish = null
        if (user !== this.userId || !trainingFeatureAccess.isAllowed()) continue
        try {
          const result = await this.call(payload)
          if (result?.accepted !== true) this.lastSignature = null
        } catch (_) { this.lastSignature = null }
      }
    } finally { this.publishingPhone = false }
  }
  async receivePhoneCommand(command) {
    if (!this.phoneEntrySupported) return
    let accepted = false, message
    try {
      const state = store.state.runWorkoutStore
      const snapshot = phoneWatchSnapshot(state, this.userId, this.phoneSnapshot)
      const change = phoneWatchCommand(state, snapshot, command)
      if (!change.duplicate) {
        if (change.enableEntry) {
          if (state.watchCompanion?.clientSessionId !== state.liveSession.clientSessionId ||
            !["running", "paused", "starting", "waiting_for_sensor"].includes(state.watchCompanion.status)) {
            store.commit("runWorkoutStore/SET_WATCH_COMPANION_STATE", {
              clientSessionId: state.liveSession.clientSessionId, platform: "apple_watch", status: "starting", error: null })
          }
        } else if (change.stopRest) store.commit("runWorkoutStore/STOP_TIMER")
        else {
          store.commit("runWorkoutStore/ADD_COMPLETED_SET", change)
          if (change.duration > 0) {
            store.commit("runWorkoutStore/START_TIMER", { ...change, exerciseIndex: change.idx })
            store.commit("runWorkoutStore/MINIMIZE_TIMER")
          } else store.commit("runWorkoutStore/STOP_TIMER")
        }
        if (!change.enableEntry) store.commit("runWorkoutStore/SET_LIVE_FEEDBACK_REST", null)
        store.commit("runWorkoutStore/SET_LIVE_SESSION", {
          phoneWatchRequests: [...(state.liveSession.phoneWatchRequests || []), command.requestId].slice(-100) })
      }
      accepted = true
    } catch (error) { message = error.message }
    // Reply with current rows as well: a lost push must not leave a saved set on
    // the old entry screen, and a conflict must show the phone's current slot.
    let snapshot = null
    try { snapshot = phoneWatchSnapshot(store.state.runWorkoutStore, this.userId, this.phoneSnapshot) } catch (_) {}
    this.phoneSnapshot = snapshot
    this.lastSignature = null
    this.schedule()
    await this.call({ action: "phoneCommandResult", requestId: command.requestId, accepted, message, state: snapshot }).catch(() => {})
  }
  applyShared(snapshot, linked = {}) {
    const state = store.state.runWorkoutStore
    const projection = sharedWatchProjection(snapshot, state)
    if (!projection) return false
    store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { snapshot, pending: null, error: null, conflict: false, ...linked })
    store.commit("runWorkoutStore/APPLY_LINKED_WATCH_WORKOUT", projection)
    return true
  }
  receiveShared(snapshot) {
    const state = store.state.runWorkoutStore
    if (!snapshot.workout) return
    if (state.activeSplit && state.liveSession?.clientSessionId !== snapshot.clientSessionId) {
      const finished = state.liveSession?.owner === "watch" && ["completed", "discarded"].includes(state.liveSession.phase)
      if (!finished || state.linkedWatchWorkout?.pending || terminal(snapshot) || Date.now() - snapshot.updatedAt > 120000 || !sharedWatchProjection(snapshot)) return
      store.commit("runWorkoutStore/RESET_WORKOUT_STATE")
    }
    if (!state.activeSplit && (terminal(snapshot) || Date.now() - snapshot.updatedAt > 120000)) return
    const linked = state.linkedWatchWorkout
    if (!linked?.pending) {
      if (linked?.error) store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...linked, remote: snapshot })
      else this.applyShared(snapshot)
      return
    }
    const pending = linked.pending
    const receipt = snapshot.workout.receipts?.[pending.requestId]
    if (!receipt) {
      store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...linked, remote: snapshot,
        error: terminal(snapshot) ? "Das Training wurde auf der Uhr beendet. Die offenen Handy-Eingaben wurden noch nicht bestätigt." : linked.error })
      return
    }
    let draft
    try { draft = sharedWatchRows(state) } catch (error) { this.sharedError(error.message); return }
    const base = receipt === "accepted" && pending.operation === "edit" ? pending.rows : pending.baseRows
    const merge = mergeWatchRows(base, draft, snapshot.workout.rows)
    if ((receipt !== "accepted" && receipt !== "conflict") || merge.conflict) {
      store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...linked, remote: snapshot, conflict: true,
        error: receipt === "paused" ? "Das Training ist auf der Uhr pausiert. Bitte dort fortsetzen." : "Dieser Satz wurde gleichzeitig auf der Uhr geändert. Welche Eingabe soll gelten?" })
      return
    }
    if (!sameWatchRows(merge.rows, snapshot.workout.rows) && !terminal(snapshot)) {
      this.applyShared({ ...snapshot, workout: { ...snapshot.workout, rows: merge.rows } })
      store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...state.linkedWatchWorkout, snapshot })
      this.queueShared("edit", merge.rows)
    } else {
      this.applyShared(snapshot)
      if (receipt === "conflict" && ["finish", "discard", "stopRest"].includes(pending.operation) && !terminal(snapshot)) this.queueShared(pending.operation)
    }
  }
  sharedError(message) {
    const linked = store.state.runWorkoutStore.linkedWatchWorkout
    if (linked) store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...linked, error: message })
  }
  sharedChanged(mutation) {
    const state = store.state.runWorkoutStore
    if (state.liveSession?.owner !== "watch" || !state.linkedWatchWorkout || this.sharedScheduled) return
    if (mutation.type.endsWith("STOP_TIMER") && state.linkedWatchWorkout.snapshot.restEndsAt > Date.now()) this.sharedStopRest = true
    this.sharedScheduled = true
    Promise.resolve().then(() => {
      this.sharedScheduled = false
      const current = store.state.runWorkoutStore
      const linked = current.linkedWatchWorkout
      if (!linked || current.liveSession?.owner !== "watch" || terminal(linked.snapshot) || !trainingFeatureAccess.isAllowed()) return
      try {
        const rows = sharedWatchRows(current)
        if (linked.pending) return // Subsequent edits stay in Vuex and are rebased after acknowledgement.
        if (!sameWatchRows(rows, linked.snapshot.workout.rows)) this.queueShared("edit", rows)
        else if (this.sharedStopRest) this.queueShared("stopRest")
        this.sharedStopRest = false
      } catch (error) { this.sharedError(error.message) }
    })
  }
  queueShared(operation, rows) {
    const state = store.state.runWorkoutStore
    const linked = state.linkedWatchWorkout
    if (!linked || linked.pending || terminal(linked.snapshot) || !trainingFeatureAccess.isAllowed()) return false
    const exercise = state.activeSplit?.exercises?.[state.selectedExerciseIndex]
    const child = exercise?.isSuperset ? exercise.exercises[state.activeSupersetSubIndex || 0] : exercise
    const restStartedAt = state.liveFeedbackRest?.startedAt || (state.timerState?.isActive ? state.timerState.startTime : null)
    const restEndsAt = state.liveFeedbackRest?.endsAt || (restStartedAt ? restStartedAt + state.timerState.duration * 1000 : null)
    const pending = { requestId: createTrainingSessionId(), clientSessionId: state.liveSession.clientSessionId,
      operation, baseToken: linked.snapshot.workout.token, baseRows: linked.snapshot.workout.rows,
      ...(rows ? { rows } : {}), selectedInstanceId: child?.sets?.[0]?.[0]?._watch?.instanceId,
      restSeconds: state.timerState?.isActive ? Math.round(state.timerState.duration || 0) : undefined,
      restStartedAt, restEndsAt }
    const { baseRows, ...wireCommand } = pending
    if (new TextEncoder().encode(JSON.stringify(wireCommand)).length > 63000) { this.sharedError("Dieses Training ist zu gross für den direkten Uhren-Abgleich."); return false }
    store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...linked, pending, error: null, conflict: false })
    this.sendShared(true)
    return true
  }
  async sendShared(force = false) {
    const state = store.state.runWorkoutStore
    const linked = state.linkedWatchWorkout
    const pending = linked?.pending
    if (!pending || linked.conflict || this.sharedSending || !this.view.supported || !trainingFeatureAccess.isAllowed() ||
      state.liveSession?.userId !== this.userId || (!force && Date.now() - (this.sharedSentAt || 0) < 14000)) return
    this.sharedSending = true; this.sharedSentAt = Date.now()
    try {
      const { baseRows, ...command } = pending
      const response = await this.call({ action: "command", command })
      if (store.state.runWorkoutStore.linkedWatchWorkout?.pending?.requestId === pending.requestId && response?.accepted !== true) {
        this.sharedError("Noch keine Verbindung zur Uhr. Deine Eingabe bleibt auf dem Handy erhalten.")
      }
    } catch (_) {
      if (store.state.runWorkoutStore.linkedWatchWorkout?.pending?.requestId === pending.requestId) this.sharedError("Der Abgleich steht noch aus. Deine Eingabe bleibt auf dem Handy erhalten.")
    } finally {
      this.sharedSending = false
      // An ACK can arrive before the bridge call resolves. Drain the next edit
      // immediately instead of leaving it to the 15-second retry heartbeat.
      const next = store.state.runWorkoutStore.linkedWatchWorkout?.pending
      if (next && next.requestId !== pending.requestId) this.sendShared(true)
    }
  }
  resolveShared(usePhone) {
    const state = store.state.runWorkoutStore, linked = state.linkedWatchWorkout
    if (!linked) return
    const remote = linked.remote || this.view.state || linked.snapshot
    if (usePhone && !terminal(remote)) {
      const draft = sharedWatchRows(state)
      const merged = mergeWatchRows(linked.pending?.baseRows || linked.snapshot.workout.rows, draft, remote.workout.rows, true)
      this.applyShared({ ...remote, workout: { ...remote.workout, rows: merged.rows } })
      store.commit("runWorkoutStore/SET_LINKED_WATCH_WORKOUT", { ...state.linkedWatchWorkout, snapshot: remote })
      this.queueShared("edit", merged.rows)
    } else this.applyShared(remote)
  }
  async finishShared(operation = "finish") {
    const state = store.state.runWorkoutStore
    const id = state.liveSession?.clientSessionId
    if (state.liveSession?.owner !== "watch" || !id) throw new Error("Kein gemeinsames Training geöffnet.")
    const access = trainingFeatureAccess.capture()
    const waitFor = (condition) => new Promise((resolve, reject) => {
      let stop, timeout
      const check = () => {
        const current = store.state.runWorkoutStore
        let error
        if (!trainingFeatureAccess.isCurrent(access) || current.liveSession?.clientSessionId !== id) error = "Das angemeldete Training hat gewechselt."
        else if (current.linkedWatchWorkout?.conflict || current.linkedWatchWorkout?.error) error = current.linkedWatchWorkout.error || "Bitte zuerst die offenen Eingaben abgleichen."
        if (error || condition(current)) { clearTimeout(timeout); stop?.(); error ? reject(new Error(error)) : resolve() }
      }
      stop = store.watch(() => [store.state.runWorkoutStore.linkedWatchWorkout, store.state.runWorkoutStore.liveSession], check)
      timeout = setTimeout(() => { stop(); reject(new Error("Die Uhr hat noch nicht bestätigt. Bitte Verbindung prüfen und erneut versuchen.")) }, 20000)
      check()
    })
    await Promise.resolve()
    await waitFor((current) => !current.linkedWatchWorkout?.pending)
    if (["completed", "discarded"].includes(state.liveSession.phase) || state.liveSession.savedAt) return
    const rows = sharedWatchRows(state)
    if (!sameWatchRows(rows, state.linkedWatchWorkout.snapshot.workout.rows)) {
      if (!this.queueShared("edit", rows)) throw new Error("Bitte zuerst die offenen Eingaben abgleichen.")
      await waitFor((current) => !current.linkedWatchWorkout?.pending)
    }
    if (!this.queueShared(operation)) throw new Error("Die Uhr ist noch nicht bereit.")
    await waitFor((current) => ["completed", "discarded"].includes(current.liveSession.phase) || Boolean(current.liveSession.savedAt))
  }

  openAdd(id) {
    if (!trainingFeatureAccess.isAllowed()) return false
    const phone = store.state.runWorkoutStore.liveSession
    const onPhone = phone?.clientSessionId === id && !phone.savedAt && phone.phase !== "ending"
    const onWatch = this.view.state?.clientSessionId === id && !terminal(this.view.state)
    if (!onPhone && !onWatch) {
      this.call({ action: "handled", clientSessionId: id }).catch(() => {})
      return false
    }
    if (onPhone && phone.owner === "watch") {
      this.router?.push({ path: "/workout/run/split", query: { addExercise: id } }).catch(() => {})
      this.call({ action: "handled", clientSessionId: id }).catch(() => {})
      return true
    }
    if (this.view.addSessionId !== id) this.pendingCommand = null
    this.view.addSessionId = id
    this.view.error = null
    this.call({ action: "handled", clientSessionId: id }).catch(() => {})
    return true
  }
  cancelAdd() { this.view.addSessionId = null; this.view.error = null; this.pendingCommand = null }
  async addExercise(exercise) {
    const id = this.view.addSessionId
    if (!trainingFeatureAccess.isAllowed() || !id) throw new Error("Das Training ist nicht mehr geöffnet.")
    const workout = store.state.runWorkoutStore
    if (workout.liveSession?.clientSessionId === id && !workout.liveSession.savedAt && workout.liveSession.phase !== "ending") {
      await store.dispatch("runWorkoutStore/addExerciseTemporarily", { newExercise: exercise, atIndex: workout.activeSplit.exercises.length })
      this.cancelAdd(); return
    }
    if (this.view.state?.clientSessionId !== id || terminal(this.view.state)) throw new Error("Dieses Training wurde auf der Uhr bereits beendet.")
    // Preserve the command and instance IDs after timeout: retry must not append twice.
    const fingerprint = JSON.stringify(exercise)
    if (!this.pendingCommand || this.pendingCommand.fingerprint !== fingerprint) {
      const requestId = createTrainingSessionId()
      this.pendingCommand = { fingerprint, requestId, clientSessionId: id, exercises: buildLinkedWatchExercises(exercise, requestId) }
    }
    const command = this.pendingCommand
    if (!command.exercises.length) throw new Error("Die Übung enthält keine gültigen Sätze.")
    const access = trainingFeatureAccess.capture()
    const response = await this.call({ action: "add", command: { requestId: command.requestId, clientSessionId: id, exercises: command.exercises } })
    if (response?.accepted !== true) throw new Error("Die Uhr ist noch nicht bereit. Bitte erneut versuchen.")
    await new Promise((resolve, reject) => {
      let stop, timeout
      const check = () => {
        const state = this.view.state
        let error
        if (!trainingFeatureAccess.isCurrent(access) || this.view.addSessionId !== id) error = "Die Übungsauswahl wurde geschlossen."
        else if (state?.clientSessionId !== id || terminal(state)) error = "Das Training auf der Uhr wurde beendet."
        const confirmed = !error && command.exercises.every((item) => state.exercises?.some((row) => row.instanceId === item.instanceId))
        if (error || confirmed) { clearTimeout(timeout); stop?.(); error ? reject(new Error(error)) : resolve() }
      }
      stop = store.watch(() => [this.view.state, this.view.addSessionId], check)
      timeout = setTimeout(() => { stop(); reject(new Error("Noch keine Bestätigung der Uhr. Verbindung prüfen und erneut versuchen.")) }, 15000)
      check()
    })
    this.cancelAdd()
  }
}
export default new WatchWorkoutLink()
